import React from 'react';
import { TrendingProject, TrendingDeveloper, Repository } from '../../types';
import { useAppStore } from '../../store';

interface TrendingProjectCardProps {
  project: TrendingProject;
}

const TrendingProjectCard: React.FC<TrendingProjectCardProps> = ({ project }) => {
  const { isAuthenticated, toggleFavorite, isFavorited } = useAppStore();

  const formatNumber = (num: number): string => {
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'k';
    }
    return num.toString();
  };

  // 检查项目是否已收藏
  const isProjectFavorited = isFavorited(project.full_name);

  // 将TrendingProject转换为Repository类型以便收藏
  const convertToRepository = (trendingProject: TrendingProject): Repository => {
    return {
      name: trendingProject.name,
      full_name: trendingProject.full_name,
      description: trendingProject.description,
      stars: trendingProject.stars,
      forks: trendingProject.forks,
      url: trendingProject.url,
      owner: trendingProject.owner,
      owner_avatar: trendingProject.owner_avatar || '',
      created_at: trendingProject.date, // 使用trending date作为created_at
      updated_at: trendingProject.date, // 使用trending date作为updated_at
      language: trendingProject.language,
      topics: [] // trending项目没有topics信息
    };
  };

  const handleProjectClick = () => {
    window.open(project.url, '_blank');
  };

  const handleOwnerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(`https://github.com/${project.owner}`, '_blank');
  };

  const handleDeveloperClick = (e: React.MouseEvent, developer: TrendingDeveloper) => {
    e.stopPropagation();
    window.open(developer.href, '_blank');
  };

  const handleToggleFavorite = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!isAuthenticated) {
      alert('请先登录后再使用收藏功能');
      return;
    }

    const repositoryData = convertToRepository(project);

    // 调试信息：检查数据完整性
    console.log('🔍 收藏数据检查:', {
      name: repositoryData.name,
      full_name: repositoryData.full_name,
      url: repositoryData.url,
      description: repositoryData.description,
      stars: repositoryData.stars,
      language: repositoryData.language
    });

    // 检查必需字段
    if (!repositoryData.url) {
      console.error('❌ 项目URL缺失:', project);
      alert('项目数据不完整，无法收藏');
      return;
    }

    await toggleFavorite(repositoryData);
  };

  // AI解读区域现在固定显示5行，超出部分滚动

  return (
    <div className="trending-project-card" onClick={handleProjectClick}>
      {/* 项目头部 */}
      <div className="project-header">
        <div className="project-info">
          <h3 className="project-name">
            {project.name}
          </h3>
          {/* 项目所有者信息移到这里 */}
          <span className="meta-item owner" onClick={handleOwnerClick}>
            {project.owner_avatar && (
              <img
                src={project.owner_avatar}
                alt={project.owner}
                className="owner-avatar"
              />
            )}
            <span>{project.owner}</span>
          </span>
        </div>
      </div>

      {/* 项目描述 - 只在没有AI解读时显示 */}
      {!project.ai_interpretation && project.description && (
        <div className="project-description">
          <p>{project.description}</p>
        </div>
      )}

      {/* AI解读区域 - 固定5行高度，超出滚动 */}
      <div className="project-ai-interpretation">
        <div className="ai-header">
          <i className="fas fa-brain"></i>
          <span>AI解读</span>
        </div>
        <div className="ai-content-container">
          {project.ai_interpretation ? (
            <div className="ai-content">
              <p>{project.ai_interpretation}</p>
            </div>
          ) : (
            <div className="ai-content ai-placeholder">
              <p>暂无AI解读</p>
            </div>
          )}
        </div>
      </div>

      {/* Built by 开发者列表 */}
      {project.built_by && project.built_by.length > 0 && (
        <div className="project-built-by">
          <span className="built-by-label">Built by</span>
          <div className="developers-list">
            {project.built_by.slice(0, 5).map((developer: TrendingDeveloper, index: number) => (
              <div
                key={index}
                className="developer-item"
                onClick={(e) => handleDeveloperClick(e, developer)}
                title={developer.username}
              >
                <img
                  src={developer.avatar}
                  alt={developer.username}
                  className="developer-avatar"
                />
              </div>
            ))}
            {project.built_by.length > 5 && (
              <span className="more-developers">
                +{project.built_by.length - 5}
              </span>
            )}
          </div>
        </div>
      )}

      {/* 项目元信息 - 移到底部 */}
      <div className="project-meta">
        {/* 编程语言 */}
        {project.language && (
          <span className="meta-item language">
            <span className="language-dot" style={{ backgroundColor: getLanguageColor(project.language) }}></span>
            {project.language}
          </span>
        )}

        {/* Stars 和 Forks 移到这里 */}
        <div className="project-stats">
          <span className="stat-item">
            <i className="fas fa-star"></i>
            {formatNumber(project.stars)}
          </span>
          <span className="stat-item">
            <i className="fas fa-code-branch"></i>
            {formatNumber(project.forks)}
          </span>
        </div>
      </div>

      {/* 卡片底部 */}
      <div className="project-footer">
        <span className="project-date">
          <i className="fas fa-calendar"></i>
          {new Date(project.date).toLocaleDateString('zh-CN')}
        </span>

        <div className="project-actions">
          <button
            className={`action-btn-small ${isProjectFavorited ? 'favorited' : ''}`}
            onClick={handleToggleFavorite}
            title={isProjectFavorited ? '取消收藏' : '收藏项目'}
            disabled={!isAuthenticated}
          >
            <i className="fas fa-heart"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

// 获取编程语言对应的颜色
const getLanguageColor = (language: string): string => {
  const colors: { [key: string]: string } = {
    'JavaScript': '#f1e05a',
    'TypeScript': '#2b7489',
    'Python': '#3572A5',
    'Java': '#b07219',
    'C++': '#f34b7d',
    'C': '#555555',
    'C#': '#239120',
    'PHP': '#4F5D95',
    'Ruby': '#701516',
    'Go': '#00ADD8',
    'Rust': '#dea584',
    'Swift': '#ffac45',
    'Kotlin': '#F18E33',
    'Dart': '#00B4AB',
    'HTML': '#e34c26',
    'CSS': '#1572B6',
    'Vue': '#4FC08D',
    'React': '#61DAFB',
    'Shell': '#89e051',
    'Dockerfile': '#384d54',
    'Jupyter Notebook': '#DA5B0B'
  };
  
  return colors[language] || '#586069';
};

export default TrendingProjectCard;
